import { useState, useRef, useLayoutEffect } from 'react';
import { Tooltip } from 'antd';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';

export const ResizableTitle = (
  props: React.HTMLAttributes<HTMLTableCellElement> & {
    onResize?: (e: React.SyntheticEvent, data: { size: { width: number } }) => void;
    width?: number;
  },
) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="resizable-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} style={{ ...restProps.style, overflow: 'visible' }} />
    </Resizable>
  );
};

const sharedCanvas = (() => {
  let canvas: HTMLCanvasElement | null = null;
  return () => {
    if (!canvas) canvas = document.createElement('canvas');
    return canvas.getContext('2d')!;
  };
})();

const calcMiddleEllipsis = (text: string, availableWidth: number, font: string) => {
  const ctx = sharedCanvas();
  ctx.font = font;
  if (ctx.measureText(text).width <= availableWidth) return { display: text, tipped: false };

  const dots = '...';
  const dotsW = ctx.measureText(dots).width;
  const budget = availableWidth - dotsW;
  if (budget <= 0) return { display: dots, tipped: true };

  let head = '';
  for (let i = 0; i < text.length; i++) {
    if (ctx.measureText(text.slice(0, i + 1)).width > budget * 0.5) break;
    head = text.slice(0, i + 1);
  }
  let tail = '';
  for (let i = text.length; i > 0; i--) {
    if (ctx.measureText(text.slice(i)).width > budget * 0.5) break;
    tail = text.slice(i);
  }
  return { display: head + dots + tail, tipped: true };
};

export const MiddleEllipsis = ({ text, style }: { text: string; style?: React.CSSProperties }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const prevResult = useRef('');
  const [info, setInfo] = useState({ display: text, tipped: false });

  useLayoutEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const td = el.closest('td') as HTMLElement | null;
    if (!td) return;

    const s = getComputedStyle(td);
    const avail = td.clientWidth - parseFloat(s.paddingLeft) - parseFloat(s.paddingRight);
    if (avail <= 0) return;

    const result = calcMiddleEllipsis(text, avail, getComputedStyle(el).font);
    if (result.display !== prevResult.current) {
      prevResult.current = result.display;
      setInfo(result);
    }
  });

  const inner = (
    <span
      ref={spanRef}
      style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', ...style }}
    >
      {info.display}
    </span>
  );

  return info.tipped ? <Tooltip title={text} placement="topLeft">{inner}</Tooltip> : inner;
};

export const useResizableColumns = (initialWidths: Record<string, number>) => {
  const [columnWidths, setColumnWidths] = useState(initialWidths);

  const handleColumnResize = (key: string) =>
    (_: React.SyntheticEvent, { size }: { size: { width: number } }) => {
      setColumnWidths((prev) => ({ ...prev, [key]: size.width }));
    };

  const resizeHeaderCell = (key: string) =>
    () => ({ width: columnWidths[key], onResize: handleColumnResize(key) }) as any;

  // setColumnWidths 也对外暴露：列宽除用户拖拽外，还有「按数据形态自适应」的场景
  //（如 JobTaskList 的仿真结果列，只在感知 Job 下加宽以容纳 checker 标签）。
  return { columnWidths, setColumnWidths, resizeHeaderCell };
};
